import { TRPC_ERROR_CODE_KEY } from '@trpc/server/unstable-core-do-not-import';
import { z } from 'zod';

import translation from '../i18n/locales/en.json';

export interface ApiError {
  errorKey?: keyof IntlMessages;
  errorKeyParameters?: Record<string, string | number>;
  message?: string | null;
}

const intlMessageKeys = Object.keys(translation) as [keyof typeof translation];

export const ApiErrorSchema = z.object({
  errorKey: z.enum(intlMessageKeys).optional(),
  errorKeyParameters: z
    .record(z.union([z.string(), z.number(), z.null()]))
    .optional()
    .transform((params) => {
      if (!params) {
        return undefined;
      }

      const filtered = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value != null),
      ) as Record<string, string | number>;

      return Object.keys(filtered).length > 0 ? filtered : undefined;
    }),
  message: z.string().nullish(),
});

const isErrorWithErrorKey = (error: unknown): error is ApiError =>
  typeof error === 'object' &&
  error !== null &&
  'errorKey' in error &&
  typeof error['errorKey'] === 'string';

const isErrorWithMessage = (error: unknown): error is ApiError =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof error['message'] === 'string';

export const isApiError = (error: unknown): error is ApiError =>
  isErrorWithErrorKey(error) || isErrorWithMessage(error);

interface ErrorResponse {
  errors: ApiError[];
}

const isErrorResponse = (body: unknown): body is ErrorResponse =>
  typeof body === 'object' &&
  body !== null &&
  'errors' in body &&
  Array.isArray(body['errors']);

export const getErrorFromBody = (body: unknown): string | undefined => {
  if (!isErrorResponse(body) || body['errors']?.length === 0) {
    if (
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof body.message === 'string'
    ) {
      return body.message;
    }

    return '';
  }

  const firstError = body['errors'][0];

  if (isErrorWithErrorKey(firstError)) {
    return body['errors'][0]['errorKey'];
  }

  if (isErrorWithMessage(firstError)) {
    return body['errors'][0]['message'] ?? '';
  }

  return '';
};

export const getFirstError = (body: unknown): ApiError | undefined => {
  if (!isErrorResponse(body) || body.errors.length === 0) {
    return undefined;
  }

  const parsed = ApiErrorSchema.safeParse(body.errors[0]);

  if (!parsed.success) {
    return { errorKey: 'error_general' };
  }

  return parsed.data;
};

const trpcErrorForStatus: Record<number, TRPC_ERROR_CODE_KEY> = {
  '400': 'BAD_REQUEST',
  '401': 'UNAUTHORIZED',
  '403': 'FORBIDDEN',
  '404': 'NOT_FOUND',
  '405': 'METHOD_NOT_SUPPORTED',
  '408': 'TIMEOUT',
  '409': 'CONFLICT',
  '412': 'PRECONDITION_FAILED',
  '413': 'PAYLOAD_TOO_LARGE',
  '415': 'UNSUPPORTED_MEDIA_TYPE',
  '422': 'UNPROCESSABLE_CONTENT',
  '429': 'TOO_MANY_REQUESTS',
  '499': 'CLIENT_CLOSED_REQUEST',
  '500': 'INTERNAL_SERVER_ERROR',
  '501': 'NOT_IMPLEMENTED',
};

export const getTrpcErrorCodeForHttpStatusCode = (
  statusCode: number,
): TRPC_ERROR_CODE_KEY =>
  trpcErrorForStatus[statusCode] ?? 'INTERNAL_SERVER_ERROR';

export const INVALID_ACCESS_TOKEN_MESSAGE = 'Invalid Access Token';

export const AUTH0_VALIDATION_ERROR_MESSAGE = 'Auth0 validation error';
